import React, { useEffect, useState } from 'react';
import { ShieldAlert, Loader2, RefreshCw, Users, ShieldOff, ShieldCheck, Ban } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';

export default function IPDuplicatesSection() {
  const [rows, setRows] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIp, setBusyIp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, b] = await Promise.all([
        api.get('/admin/ip-duplicates'),
        api.get('/admin/blocked-ips'),
      ]);
      setRows(d.data);
      setBlocked(b.data);
    } catch {
      setRows([]); setBlocked([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const blockIp = async (ip) => {
    setBusyIp(ip);
    try {
      await api.post('/admin/block-ip', { ip });
      toast({ title: `IP ${ip} bloqueada` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error al bloquear' });
    } finally { setBusyIp(null); }
  };

  const unblockIp = async (ip) => {
    setBusyIp(ip);
    try {
      await api.delete(`/admin/block-ip/${encodeURIComponent(ip)}`);
      toast({ title: `IP ${ip} desbloqueada` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error al desbloquear' });
    } finally { setBusyIp(null); }
  };

  const blockedSet = new Set(blocked.map((b) => b.ip));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-rose-400" /> IPs Duplicadas
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Direcciones IP asociadas a más de un usuario — incluye IP de registro y último inicio de sesión.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ip-duplicates-refresh"
          className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Users className="w-6 h-6 text-emerald-400" />
          </div>
          <p className="text-white/40 text-sm">Ninguna IP compartida entre usuarios.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ip-duplicates-list">
          {rows.map((row) => {
            const isBlocked = blockedSet.has(row.ip);
            const busy = busyIp === row.ip;
            return (
              <div
                key={row.ip}
                data-testid={`ip-dup-${row.ip}`}
                className={`rounded-2xl px-5 py-4 border ${isBlocked ? 'bg-rose-500/5 border-rose-500/20' : 'bg-neutral-900 border-rose-400/20'}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isBlocked ? 'bg-rose-500/10 border border-rose-500/25' : 'bg-rose-500/10 border border-rose-400/25'}`}>
                      {isBlocked ? <Ban className="w-4 h-4 text-rose-400" /> : <ShieldAlert className="w-4 h-4 text-rose-400" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-mono font-bold text-sm">{row.ip}</p>
                        <a
                          href={`https://who.is/whois-ip/ip-address/${row.ip}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`ip-whois-${row.ip}`}
                          className="text-[11px] font-bold text-sky-400 hover:text-sky-300 underline underline-offset-2 transition"
                        >
                          Consultar IP
                        </a>
                      </div>
                      <p className="text-white/40 text-xs mt-0.5">{row.count} cuentas vinculadas</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-black text-lg text-rose-400">{row.count}</span>
                    <button
                      onClick={() => isBlocked ? unblockIp(row.ip) : blockIp(row.ip)}
                      disabled={busy}
                      data-testid={`ip-dup-${isBlocked ? 'unblock' : 'block'}-${row.ip}`}
                      className={`h-8 px-3 rounded-full text-xs font-bold flex items-center gap-1.5 transition ${
                        isBlocked
                          ? 'bg-white/10 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-400'
                          : 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border border-rose-500/30'
                      }`}
                    >
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" />
                        : isBlocked ? <><ShieldCheck className="w-3 h-3" />Desbloquear</>
                        : <><ShieldOff className="w-3 h-3" />Bloquear</>}
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(row.users || []).map((u) => (
                    <span
                      key={u.email}
                      className="text-[11px] font-mono bg-white/5 border border-white/10 text-white/70 px-2.5 py-1 rounded-full"
                    >
                      {[u.name, u.last_name].filter(Boolean).join(' ') || u.email}
                      {u.name && <span className="text-white/35 ml-1">· {u.email}</span>}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
